import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MediumPriorityFeatures.css';

const MediumPriorityFeatures = ({ recipeId, menuItemId, businessId, onClose }) => {
  const [activeTab, setActiveTab] = useState('decomposition');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Atomic Decomposition State
  const [compositeIngredient, setCompositeIngredient] = useState('');
  const [quantity, setQuantity] = useState('');
  const [decompositionResult, setDecompositionResult] = useState(null);
  const [templates, setTemplates] = useState([]);

  // Sanity Check State
  const [sanityCheckResult, setSanityCheckResult] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [dashboard, setDashboard] = useState(null);

  // OCR State
  const [ocrText, setOcrText] = useState('');
  const [ocrResult, setOcrResult] = useState(null);
  const [supportedLanguages, setSupportedLanguages] = useState([]);

  const API_BASE = `${process.env.REACT_APP_API_URL || 'https://be.invexis.co.in/api'}/medium-features`;

  useEffect(() => {
    loadInitialData();
  }, [activeTab]);

  const loadInitialData = async () => {
    try {
      if (activeTab === 'decomposition') {
        await loadTemplates();
      } else if (activeTab === 'sanity') {
        await loadSanityCheck();
        await loadAlerts();
        await loadDashboard();
      } else if (activeTab === 'ocr') {
        await loadSupportedLanguages();
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  // ============================================================
  // ATOMIC DECOMPOSITION FUNCTIONS
  // ============================================================

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE}/decomposition/templates`, {
        params: { business_id: businessId }
      });
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const detectComposite = async () => {
    if (!compositeIngredient) {
      setError('Please enter an ingredient name');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_BASE}/decomposition/detect`, {
        ingredient_name: compositeIngredient,
        quantity: parseFloat(quantity) || 100,
        business_id: businessId
      });

      setDecompositionResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to detect composite ingredient');
    } finally {
      setLoading(false);
    }
  };

  const applyDecomposition = async (templateId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_BASE}/decomposition/apply`, {
        recipe_id: recipeId,
        composite_ingredient_id: decompositionResult?.composite_item_id,
        template_id: templateId,
        business_id: businessId
      });

      alert('Decomposition applied successfully! Recipe updated with atomic ingredients.');
      setDecompositionResult(null);
      setCompositeIngredient('');
      setQuantity('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply decomposition');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // SANITY CHECK FUNCTIONS
  // ============================================================

  const loadSanityCheck = async () => {
    if (!recipeId) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/sanity-check/recipe/${recipeId}`, {
        business_id: businessId
      });

      setSanityCheckResult(response.data);
    } catch (err) {
      console.error('Error loading sanity check:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    try {
      const response = await axios.get(`${API_BASE}/sanity-check/alerts`, {
        params: { business_id: businessId }
      });

      setAlerts(response.data.alerts || []);
    } catch (err) {
      console.error('Error loading alerts:', err);
    }
  };

  const loadDashboard = async () => {
    try {
      const response = await axios.get(`${API_BASE}/sanity-check/dashboard`, {
        params: { business_id: businessId }
      });

      setDashboard(response.data);
    } catch (err) {
      console.error('Error loading dashboard:', err);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await axios.post(`${API_BASE}/sanity-check/alert/${alertId}/acknowledge`, {
        acknowledged_by: businessId
      });

      await loadAlerts();
    } catch (err) {
      setError('Failed to acknowledge alert');
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      await axios.post(`${API_BASE}/sanity-check/alert/${alertId}/resolve`, {
        resolved_by: businessId,
        resolution_notes: 'Manually resolved'
      });

      await loadAlerts();
    } catch (err) {
      setError('Failed to resolve alert');
    }
  };

  // ============================================================
  // OCR FUNCTIONS
  // ============================================================

  const loadSupportedLanguages = async () => {
    try {
      const response = await axios.get(`${API_BASE}/ocr/supported-languages`);
      setSupportedLanguages(response.data.languages || []);
    } catch (err) {
      console.error('Error loading languages:', err);
    }
  };

  const processOCR = async () => {
    if (!ocrText) {
      setError('Please enter text to translate');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_BASE}/ocr/process-scan`, {
        ocr_text: ocrText,
        business_id: businessId
      });

      setOcrResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process OCR text');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // RENDER FUNCTIONS
  // ============================================================

  const renderDecompositionTab = () => (
    <div className="mpf-tab-content">
      <h3>🧪 Atomic Decomposition</h3>
      <p className="mpf-description">
        Auto-detect and break down composite ingredients (like "Garam Masala") into atomic ingredients with exact quantities.
      </p>

      <div className="mpf-form">
        <div className="mpf-form-group">
          <label>Ingredient Name</label>
          <input
            type="text"
            value={compositeIngredient}
            onChange={(e) => setCompositeIngredient(e.target.value)}
            placeholder="e.g., Garam Masala, Curry Powder"
            className="mpf-input"
          />
        </div>

        <div className="mpf-form-group">
          <label>Quantity (grams)</label>
          <input
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="100"
            className="mpf-input"
          />
        </div>

        <button
          onClick={detectComposite}
          disabled={loading}
          className="mpf-btn mpf-btn-primary"
        >
          {loading ? 'Detecting...' : '🔍 Detect & Decompose'}
        </button>
      </div>

      {decompositionResult && (
        <div className="mpf-result-box">
          <h4>
            {decompositionResult.is_composite ? '✅ Composite Detected' : '❌ Not Composite'}
          </h4>

          {decompositionResult.is_composite && decompositionResult.breakdown && (
            <>
              <div className="mpf-breakdown">
                <h5>Breakdown:</h5>
                {decompositionResult.breakdown.map((item, idx) => (
                  <div key={idx} className="mpf-breakdown-item">
                    <span className="mpf-item-name">{item.name}</span>
                    <span className="mpf-item-quantity">
                      {item.weight} {item.unit}
                    </span>
                    {item.cost && (
                      <span className="mpf-item-cost">₹{item.cost}</span>
                    )}
                  </div>
                ))}
              </div>

              {decompositionResult.template_id && (
                <button
                  onClick={() => applyDecomposition(decompositionResult.template_id)}
                  className="mpf-btn mpf-btn-success"
                  style={{ marginTop: '10px' }}
                >
                  ✨ Apply to Recipe
                </button>
              )}
            </>
          )}
        </div>
      )}

      {templates.length > 0 && (
        <div className="mpf-templates">
          <h4>📚 Available Templates ({templates.length})</h4>
          <div className="mpf-template-list">
            {templates.map((template) => (
              <div key={template.id} className="mpf-template-item">
                <div className="mpf-template-name">{template.composite_name}</div>
                <div className="mpf-template-meta">
                  {template.component_count} ingredients • 
                  {template.is_default ? ' Default' : ' Custom'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderSanityCheckTab = () => (
    <div className="mpf-tab-content">
      <h3>💰 Sanity Checks</h3>
      <p className="mpf-description">
        Automatically validate recipe costs vs selling prices to prevent financial losses.
      </p>

      {sanityCheckResult && (
        <div className={`mpf-sanity-result ${sanityCheckResult.is_profitable ? 'mpf-profitable' : 'mpf-unprofitable'}`}>
          <div className="mpf-sanity-header">
            <h4>{sanityCheckResult.is_profitable ? '✅ Profitable' : '⚠️ Unprofitable'}</h4>
          </div>

          <div className="mpf-sanity-stats">
            <div className="mpf-stat">
              <label>Ingredient Cost</label>
              <div className="mpf-stat-value">₹{sanityCheckResult.total_ingredient_cost?.toFixed(2)}</div>
            </div>

            <div className="mpf-stat">
              <label>Selling Price</label>
              <div className="mpf-stat-value">₹{sanityCheckResult.selling_price?.toFixed(2)}</div>
            </div>

            <div className="mpf-stat">
              <label>Profit Margin</label>
              <div className={`mpf-stat-value ${sanityCheckResult.profit_margin < 0 ? 'mpf-negative' : 'mpf-positive'}`}>
                {sanityCheckResult.profit_margin?.toFixed(1)}%
              </div>
            </div>
          </div>

          {sanityCheckResult.recommendation && (
            <div className="mpf-recommendation">
              <strong>💡 Recommendation:</strong> {sanityCheckResult.recommendation}
            </div>
          )}
        </div>
      )}

      {dashboard && (
        <div className="mpf-dashboard">
          <h4>📊 Business Dashboard</h4>
          <div className="mpf-dashboard-stats">
            <div className="mpf-dashboard-stat">
              <div className="mpf-dashboard-label">Total Recipes</div>
              <div className="mpf-dashboard-value">{dashboard.total_recipes}</div>
            </div>
            <div className="mpf-dashboard-stat">
              <div className="mpf-dashboard-label">Profitable</div>
              <div className="mpf-dashboard-value mpf-green">{dashboard.profitable_count}</div>
            </div>
            <div className="mpf-dashboard-stat">
              <div className="mpf-dashboard-label">Unprofitable</div>
              <div className="mpf-dashboard-value mpf-red">{dashboard.unprofitable_count}</div>
            </div>
            <div className="mpf-dashboard-stat">
              <div className="mpf-dashboard-label">Avg Margin</div>
              <div className="mpf-dashboard-value">{dashboard.average_margin?.toFixed(1)}%</div>
            </div>
          </div>
        </div>
      )}

      {alerts.length > 0 && (
        <div className="mpf-alerts">
          <h4>🚨 Active Alerts ({alerts.length})</h4>
          {alerts.map((alert) => (
            <div key={alert.id} className={`mpf-alert mpf-alert-${alert.severity}`}>
              <div className="mpf-alert-header">
                <span className="mpf-alert-severity">{alert.severity.toUpperCase()}</span>
                <span className="mpf-alert-dish">{alert.menu_item_name || 'Unknown Dish'}</span>
              </div>
              <div className="mpf-alert-message">{alert.alert_message}</div>
              <div className="mpf-alert-actions">
                <button
                  onClick={() => acknowledgeAlert(alert.id)}
                  className="mpf-btn mpf-btn-sm"
                  disabled={alert.is_acknowledged}
                >
                  {alert.is_acknowledged ? '✓ Acknowledged' : 'Acknowledge'}
                </button>
                <button
                  onClick={() => resolveAlert(alert.id)}
                  className="mpf-btn mpf-btn-sm mpf-btn-success"
                >
                  Resolve
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderOCRTab = () => (
    <div className="mpf-tab-content">
      <h3>🌐 Multilingual OCR</h3>
      <p className="mpf-description">
        Scan and translate bills/menus in Telugu, Hindi, Tamil, and Kannada to English.
      </p>

      <div className="mpf-languages">
        <h4>Supported Languages:</h4>
        <div className="mpf-language-grid">
          {supportedLanguages.map((lang) => (
            <div key={lang.language_code} className="mpf-language-badge">
              {lang.is_enabled ? '✅' : '❌'} {lang.language_name}
            </div>
          ))}
        </div>
      </div>

      <div className="mpf-form">
        <div className="mpf-form-group">
          <label>Enter Text (any language)</label>
          <textarea
            value={ocrText}
            onChange={(e) => setOcrText(e.target.value)}
            placeholder="Paste text from bill or menu in Telugu, Hindi, Tamil, or Kannada..."
            rows={6}
            className="mpf-textarea"
          />
        </div>

        <button
          onClick={processOCR}
          disabled={loading}
          className="mpf-btn mpf-btn-primary"
        >
          {loading ? 'Translating...' : '🔄 Translate to English'}
        </button>
      </div>

      {ocrResult && (
        <div className="mpf-ocr-result">
          <div className="mpf-ocr-detection">
            <strong>Detected Language:</strong> {ocrResult.language_name} ({ocrResult.detected_language})
            <span className="mpf-confidence">Confidence: {(ocrResult.confidence * 100).toFixed(0)}%</span>
          </div>

          <div className="mpf-ocr-translation">
            <h5>Original Text:</h5>
            <div className="mpf-ocr-original">{ocrResult.original_text}</div>

            <h5>Translated Text:</h5>
            <div className="mpf-ocr-translated">{ocrResult.translated_text}</div>
          </div>

          {ocrResult.matched_menu_item && (
            <div className="mpf-ocr-match">
              <strong>✅ Matched Menu Item:</strong> {ocrResult.matched_menu_item.name}
            </div>
          )}

          {ocrResult.cache_hit !== undefined && (
            <div className="mpf-ocr-cache">
              {ocrResult.cache_hit ? '💾 From cache (instant)' : '🌐 New translation'}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="mpf-modal-overlay" onClick={onClose}>
      <div className="mpf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mpf-header">
          <h2> Medium Priority Features</h2>
          <button onClick={onClose} className="mpf-close-btn">✕</button>
        </div>

        <div className="mpf-tabs">
          <button
            className={`mpf-tab ${activeTab === 'decomposition' ? 'mpf-tab-active' : ''}`}
            onClick={() => setActiveTab('decomposition')}
          >
            🧪 Decomposition
          </button>
          <button
            className={`mpf-tab ${activeTab === 'sanity' ? 'mpf-tab-active' : ''}`}
            onClick={() => setActiveTab('sanity')}
          >
            💰 Sanity Checks
          </button>
          <button
            className={`mpf-tab ${activeTab === 'ocr' ? 'mpf-tab-active' : ''}`}
            onClick={() => setActiveTab('ocr')}
          >
            🌐 OCR Translation
          </button>
        </div>

        {error && (
          <div className="mpf-error">
            ⚠️ {error}
            <button onClick={() => setError(null)} className="mpf-error-close">✕</button>
          </div>
        )}

        <div className="mpf-body">
          {activeTab === 'decomposition' && renderDecompositionTab()}
          {activeTab === 'sanity' && renderSanityCheckTab()}
          {activeTab === 'ocr' && renderOCRTab()}
        </div>
      </div>
    </div>
  );
};

export default MediumPriorityFeatures;
